/**
 * Formatação do estoque — um lugar só (molde de rework/reworkFormat.ts).
 *
 * `numeric` do Postgres chega como STRING no driver: formatar em cada tela
 * espalharia `toFixed(3)` e cada uma arredondaria de um jeito. Peso sempre com
 * vírgula decimal (é o que o operador digita e lê na balança) e sempre em
 * `tabular-nums`, para a coluna alinhar.
 */

import { LABEL_MOVIMENTO, rotuloExcecao, type ExcecaoRegistrada, type Movimento, type StockLedgerType } from '@/types/stock';
import { searchNormalize } from '@/hooks/useItemCatalog';
import type { DocumentoDaConferencia } from '@/hooks/useStockInbound';

/** Peso/quantidade vindos do banco (string) ou já numéricos. */
/**
 * A busca do módulo — UMA só. Por PALAVRAS, em qualquer ordem e sem acento:
 * "pret avental" acha "AVENTAL PRETO ÚNICO", "pv limao" acha "CAMISETA PV
 * BRANCA · Verde limão - M". Nasceu no combobox de material (2026-09-22) e
 * vale para saldos, materiais, movimentos, pendências e compras: duas telas
 * com buscas que respondem diferente ao mesmo texto é o pior resultado.
 */
export function casaBusca(alvo: string, busca: string): boolean {
  const a = searchNormalize(alvo);
  const palavras = searchNormalize(busca).split(/\s+/).filter(Boolean);
  return palavras.every((p) => a.includes(p));
}

/** Como o estoquista reconhece uma nota da conferência: o nº da compra do GC, senão o id dela. */
export const nomeDaCompra = (d: DocumentoDaConferencia) => d.numero_documento ?? d.gc_doc_ref;

export function formatarQuantidade(valor: string | number | null | undefined, unidade?: string): string {
  const n = typeof valor === 'number' ? valor : Number(valor ?? 0);
  if (!Number.isFinite(n)) return '—';
  // Peça é inteira; peso e metragem têm casas decimais.
  const casas = unidade === 'un' ? 0 : 3;
  const texto = n.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
  return unidade ? `${texto} ${unidade}` : texto;
}

/**
 * O material tem estoque mínimo de verdade?
 *
 * ⚠️ ZERO NÃO É MÍNIMO. É a regra do servidor — `stock-sweep.ts` só abre o
 * aviso "abaixo do mínimo" com `estoque_minimo IS NOT NULL AND > 0` —, e as
 * telas liam `!== null`: um material com 0 exibia "mínimo 0,000 kg", que se lê
 * como um mínimo configurado, enquanto a varredura das 06h30 o ignorava. Ler a
 * mesma regra dos dois lugares é o que impede a tela de contar uma história
 * diferente da do servidor.
 */
export function temEstoqueMinimo(valor: string | number | null | undefined): boolean {
  if (valor === null || valor === undefined || valor === '') return false;
  const n = Number(valor);
  return Number.isFinite(n) && n > 0;
}

/** Aceita vírgula e ponto: o teclado numérico manda ponto, o operador digita vírgula. */
export function parsearQuantidade(entrada: string): number | null {
  let limpo = String(entrada).trim().replace(/\s/g, '');
  if (!limpo) return null;
  // ⚠️ O SEPARADOR DE MILHAR QUE A PRÓPRIA TELA IMPRIME. `formatarQuantidade`
  // escreve "1.500,000"; o parser trocava só a vírgula por ponto e o Number
  // lia "1.500.000" como NaN — ou, pior, "1.500" digitado à mão virava 1,5 e
  // o operador dava entrada de um rolo e meio no lugar de mil e quinhentos.
  // Com vírgula decimal presente, todo ponto é milhar. Sem vírgula, o ponto só
  // é milhar quando separa exatamente três dígitos até o fim.
  if (limpo.includes(',')) {
    limpo = limpo.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(limpo)) {
    limpo = limpo.replace(/\./g, '');
  }
  const n = Number(limpo);
  return Number.isFinite(n) ? n : null;
}

/**
 * Peso lido da BALANÇA (kg, 3 casas): o ponto é SEMPRE decimal.
 *
 * ⚠️ `parsearQuantidade` trata "22.400" como 22.400 kg (milhar) — regra certa
 * para "1.500" no campo do comprador, e errada para o rolo: a balança imprime
 * "22.400" e o teclado numérico manda ponto, então um rolo de 22,4 kg entrava
 * com vinte e duas toneladas. Nenhum rolo pesa mais de 500 kg; quantidade a
 * comprar, sim. Por isso dois parsers: este para o que se pesa na balança
 * (entrada de rolo, sobra, recontagem de rolo), aquele para quantidades.
 */
export function parsearPeso(entrada: string): number | null {
  const limpo = String(entrada).trim().replace(/\s/g, '').replace(',', '.');
  if (!limpo) return null;
  const n = Number(limpo);
  return Number.isFinite(n) ? n : null;
}

/**
 * O texto que caiu num campo de PESO é um código de barras? O leitor não rouba
 * o foco de campo numérico (de propósito, `BarcodeInput`), então um bipe com o
 * cursor no peso da sobra vira dígitos ali. Letra no texto (R0009901, P0001235)
 * ou 8+ dígitos (EAN do fornecedor) nunca é um peso de rolo — peso de balança
 * tem até 6 dígitos. Quem reconhece rebipa em vez de devolver.
 */
export function pareceCodigoDeBarras(texto: string): boolean {
  const t = String(texto).trim();
  if (!t) return false;
  // Vírgula, ponto ou espaço é gente digitando peso ("3,1kg", "3,1 kg"):
  // tratar como código derrubava o painel do rolo com "código desconhecido".
  if (/[,.\s]/.test(t)) return false;
  if (/[A-Za-z]/.test(t)) return t.length >= 6;
  return t.length >= 8;
}

/**
 * Acima disto, o peso de um ROLO pede confirmação. O `parsearPeso` lê o
 * ponto como decimal, mas "22400" (a vírgula esquecida) continua sendo 22
 * toneladas — e nada barrava. Os rolos do galpão ficam na casa de 15–30 kg;
 * 80 deixa folga para o maior e ainda pega o zero a mais. Só para rolo:
 * gola, punho e aviamento em kg podem passar disso de verdade.
 */
export const PESO_ROLO_SUSPEITO_KG = 80;
/** O mesmo teto para o tecido medido em METRO: rolo de tecido tem dezenas de metros, raramente passa de 300. */
export const METRAGEM_ROLO_SUSPEITA_M = 300;

/**
 * A medida de um ROLO — o peso na balança (kg) ou os metros do tecido (m). A máquina é a mesma (etiqueta, bipe,
 * saldo = soma dos rolos); só o instrumento muda, e com ele o rótulo, o exemplo e o teto do "faltou a vírgula?".
 * Um lugar só: cada tela escrevia "Peso da balança (kg)" na mão e o tecido em metro ficava de fora.
 */
export function medidaDoRolo(unidade?: string | null) {
  return unidade === 'm'
    ? {
        unidade: 'm' as const, campo: 'Metros do rolo (m)', campoCurto: 'Metros (m)', exemplo: '52,5',
        limite: METRAGEM_ROLO_SUSPEITA_M, aviso: 'mais do que um rolo costuma ter',
        confirma: 'O rolo tem isso mesmo', confirmado: 'Confirmado: o rolo tem isso mesmo.',
        pedido: 'A metragem do rolo, em metros.', falta: 'Falta a metragem do rolo',
        faltaDica: 'Digite os metros do rolo e aperte Enter.',
      }
    : {
        unidade: 'kg' as const, campo: 'Peso da balança (kg)', campoCurto: 'Peso (kg)', exemplo: '22,400',
        limite: PESO_ROLO_SUSPEITO_KG, aviso: 'mais do que um rolo costuma pesar',
        confirma: 'O rolo pesa isso mesmo', confirmado: 'Confirmado: o rolo pesa isso mesmo.',
        pedido: 'O peso da balança, em quilos.', falta: 'Falta o peso do rolo',
        faltaDica: 'Digite o peso na balança e aperte Enter.',
      };
}

export function pesoDeRoloSuspeito(valor: number | null | undefined, unidade?: string | null): boolean {
  return valor !== null && valor !== undefined && Number.isFinite(valor) && valor > medidaDoRolo(unidade).limite;
}

/** Só a data (dd/mm/aa) — para "entrou em", onde a hora é ruído. */
/**
 * O DIA de um lançamento, como a lista de Movimentos agrupa (revisão de UX,
 * 2026-10-03): "Hoje", "Ontem" ou a data. Em Brasília — o banco é UTC, e um
 * lançamento das 22h cairia em "amanhã" sem o fuso (CLAUDE.md §5).
 */
export function rotuloDoDia(iso: string | null | undefined, agora: Date = new Date()): string {
  if (!iso) return 'Sem data';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Sem data';
  const fmt = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric' });
  const dia = fmt.format(d);
  if (dia === fmt.format(agora)) return 'Hoje';
  if (dia === fmt.format(new Date(agora.getTime() - 24 * 60 * 60 * 1000))) return 'Ontem';
  return dia;
}

/**
 * O instante (ISO, UTC) em que começou o dia de Brasília de `agora`, `diasAtras`
 * dias atrás. O Brasil não tem horário de verão desde 2019: o dia vira às 03:00
 * UTC. É o corte de "Hoje" e "7 dias" nas Exceções do dia — um lançamento das
 * 22h cairia no dia seguinte sem o fuso (CLAUDE.md §5).
 */
export function inicioDoDiaBrasilia(agora: Date = new Date(), diasAtras = 0): string {
  const local = new Date(agora.getTime() - 3 * 3_600_000);
  return new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() - diasAtras, 3, 0, 0)).toISOString();
}

export function formatarData(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Só a hora (HH:mm), para a lista agrupada por dia. */
export function formatarHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * A frase do que aconteceu — é a coluna primária da aba Movimentos.
 * Ninguém deveria precisar decorar que RETIRADA_CORTE é saída.
 */
export function descreverMovimento(m: Movimento): string {
  const qtd = formatarQuantidade(Math.abs(Number(m.qty)), m.unit);
  const numero = (m as { order_numero?: string | null }).order_numero;
  const temPedido = !!(numero || m.order_id);
  // Com pedido, o verbo muda para não repetir "pedido": "Saída para o pedido
  // 504681", "Entrada da produção do pedido EST-…", nunca "Saída para pedido
  // para um pedido".
  const VERBO_COM_PEDIDO: Partial<Record<StockLedgerType, string>> = {
    SAIDA_PEDIDO: 'Saída para o pedido',
    RETIRADA_CORTE: 'Retirada para o corte do pedido',
    DEVOLUCAO_SOBRA: 'Sobra devolvida do pedido',
    ENTRADA_PRODUCAO: 'Entrada da produção do pedido',
  };
  const rotuloBase = LABEL_MOVIMENTO[m.type as StockLedgerType] ?? m.type;
  const md = (m.metadata ?? null) as {
    reposicao?: { motivo?: string } | null;
    descarte?: { numero_pedido?: string | null } | null;
    reducao_de_tamanho?: { para?: string | null } | null;
    baixa_automatica?: { faltou?: number } | null;
    transferencia?: { de?: string | null; para?: string | null } | null;
  } | null;
  // Mudança de caixa (0139): sai de uma caixa e entra em outra — a frase diz de onde para onde. Não se estorna:
  // desfazer é mover de volta.
  if (m.type === 'TRANSFERENCIA') {
    const t = md?.transferencia;
    const sentido = t?.de && t?.para ? ` — da caixa ${t.de} para a caixa ${t.para}` : '';
    return `${LABEL_MOVIMENTO.TRANSFERENCIA}${sentido}, ${qtd}`;
  }
  // Baixa automática ao concluir a costura (§13.85): é RETIRADA_CORTE no ledger, mas quem lançou foi a
  // costura concluída, não o posto — a frase diz isso, e diz quando faltou saldo para cobrir a reserva.
  if ((m.type === 'RETIRADA_CORTE' || m.type === 'SAIDA_PEDIDO') && md?.baixa_automatica) {
    const faltou = Number(md.baixa_automatica.faltou) || 0;
    const pacoteB = (m as { package_barcode?: string | null }).package_barcode;
    return `Baixa automática ao concluir a costura — pedido ${numero ?? '(sem número)'}${pacoteB ? ` — pacote ${pacoteB}` : ''}, ${qtd}`
      + (faltou > 0 ? ` · faltaram ${formatarQuantidade(faltou, m.unit)} no saldo` : '');
  }
  // Descarte por defeito ligado a um pedido (§13.83): é um AJUSTE no ledger, mas a
  // frase conta o que aconteceu — a busca por pedido acha a perda pela linha.
  if (m.type === 'AJUSTE' && md?.descarte) {
    const pacoteD = (m as { package_barcode?: string | null }).package_barcode;
    return `Descarte por defeito — pedido ${numero ?? md.descarte.numero_pedido ?? '(sem número)'}${pacoteD ? ` — pacote ${pacoteD}` : ''}, ${qtd}`;
  }
  // RETIRADA_CORTE SEM rolo é o aviamento contado (gola, punho — §13.47),
  // entregue à costura pelo posto: "Retirada para o corte" contaria uma
  // história errada. É o mesmo tipo no ledger; só a frase muda.
  const verbo = m.type === 'RETIRADA_CORTE' && !m.unit_barcode
    ? 'Saída para o pedido'
    : VERBO_COM_PEDIDO[m.type as StockLedgerType];
  const rotulo = temPedido
    ? `${verbo ?? `${rotuloBase} — pedido`} ${numero ?? '(sem número)'}`
    // Sem pedido e sem rolo é a saída avulsa do posto — não passou pelo corte.
    : (m.type === 'RETIRADA_CORTE' && !m.unit_barcode ? 'Saída avulsa (sem pedido)' : rotuloBase);
  const pacote = (m as { package_barcode?: string | null }).package_barcode;
  const alvo = m.unit_barcode ? ` — rolo ${m.unit_barcode}` : pacote ? ` — pacote ${pacote}` : '';
  // Rolo dividido entre pedidos (§13.58): cada linha é a parte de um pedido,
  // pelas peças dele nesta malha. Sem a marca, três retiradas do mesmo rolo
  // no mesmo minuto parecem bipe repetido.
  const rateio = (m.metadata as { rateio?: { pecas?: unknown } } | null)?.rateio;
  const dividido = rateio ? ` · rolo dividido (${Number(rateio.pecas) || 0} peças)` : '';
  // Exceção registrada (§13.74): a frase do lançamento a carrega, em toda lista.
  const excecao = rotuloExcecao((m.metadata as { excecao?: Partial<ExcecaoRegistrada> } | null)?.excecao);
  // Reposição depois da separação (§13.83): a frase diz que é reposição e para que tamanho reduziu.
  const reposicao = md?.reposicao ? ' · reposição' : '';
  const reduzida = md?.reducao_de_tamanho?.para ? ` · reduzida para ${md.reducao_de_tamanho.para}` : '';
  return `${rotulo}${alvo}, ${qtd}${dividido}${reposicao}${reduzida}${excecao ? ` · exceção: ${excecao}` : ''}`;
}

export function classeVariante(v: 'entrada' | 'saida' | 'correcao'): string {
  if (v === 'entrada') return 'text-emerald-700 dark:text-emerald-400';
  if (v === 'saida') return 'text-amber-700 dark:text-amber-400';
  return 'text-muted-foreground';
}
